
import { Vue, Options } from "vue-class-component";
import StorageService from "../services/StorageService";
import ApiService from "../services/ApiService";
import { wrapper } from "../utils/wrapper";
import { useStore } from "vuex";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { uuid } from "vue-uuid";

@Options({
  components: {
    FontAwesomeIcon
  }
})
export default class Message extends Vue {
  private storageService = new StorageService();
  private apiService = new ApiService();
  globalStore = useStore();
  showModal = false;
  message = "";
  user: any = {};
  sendMessageDisabled = false;
  refreshDisabled = false;
  searchDisabled = false;
  searchHidden = true;
  searchString = "";

  // local messages array
  messages: any[] = [];

  // filteredMessages
  filteredMessages: any[] = [];

  // Runs when the page is created
  async created() {
    this.user = this.storageService.getUser();
    await this.getMessages();
    await this.addUnreadSeparator();
    this.globalStore.commit('setUnreadMessages', this.countUnreadMessages())
    for (const el of this.$refs.incomingMessage) {
      el.addEventListener('click', async ()=> await this.markMessagesAsRead())
    }
  }

  async beforeUnmount() {
    await this.markMessagesAsRead()
  }

  countUnreadMessages() {
    return this.messages.filter(message=> message.readAt == null && message.senderUsersId != this.user.usersId).length
  }
  /*
    Gets the messages from the API.
  */
  async getMessages() {
    const dataResponse = await wrapper(
      this.apiService.getMessages(this.user.patientsId)
    );
    if (dataResponse.error) {
      if (dataResponse.error.status == 403) {
        this.globalStore.commit("open", {
          type: "danger",
          title: this.$t("ALERTS.MESSAGES.GET_FORBIDDEN.FAILED.TITLE"),
          text: this.$t("ALERTS.MESSAGES.GET_FORBIDDEN.FAILED.TEXT")
        });
      } else {
        this.globalStore.commit("open", {
          type: "danger",
          title: this.$t("ALERTS.MESSAGES.GET.FAILED.TITLE"),
          text: this.$t("ALERTS.MESSAGES.GET.FAILED.TEXT")
        });
      }
    }
    if (dataResponse.data) {
      const unorderedMessages = [...dataResponse.data];

      let orderedMessages = unorderedMessages.sort((a, b) =>
        a.updatedAt < b.updatedAt ? 1 : -1
      );

      if (!this.user.canSendMessages) {
        orderedMessages = orderedMessages.filter(
          (m: any) => m.senderUsersId != this.user.usersId
        );
      }

      this.messages = orderedMessages;
      this.search();
    }
  }

  async sendMessage() {
    this.sendMessageDisabled = true;

    const m: any = {
      title: "chat_message",
      body: this.message,
      messagesStatusesId: 2,
      messageType: "direct_message",
      uuid: uuid.v4(),
      receiverUsersId: Number(this.user.therapist.usersId)
    };

    const dataResponse = await wrapper(
      this.apiService.createMessage(m, this.user.patientsId)
    );

    if (dataResponse.error) {
      if (dataResponse.error.status == 403) {
        this.globalStore.commit("open", {
          type: "danger",
          title: this.$t("ALERTS.MESSAGES.CREATE_FORBIDDEN.FAILED.TITLE"),
          text: this.$t("ALERTS.MESSAGES.CREATE_FORBIDDEN.FAILED.TEXT")
        });
      } else {
        this.globalStore.commit("open", {
          type: "danger",
          title: this.$t("ALERTS.MESSAGES.CREATE.FAILED.TITLE"),
          text: this.$t("ALERTS.MESSAGES.CREATE.FAILED.TEXT")
        });
      }
    }
    if (dataResponse.data) {
      this.messages.unshift(dataResponse.data); // adds the message to the beginning to the array
      this.search();
      this.message = "";
    }

    this.sendMessageDisabled = false;
  }

  async refresh() {
    this.refreshDisabled = true; //disables refresh button
    await this.getMessages();
    await this.addUnreadSeparator();
    this.refreshDisabled = false; //enables refresh button
  }

  async removeUnreadSeparator() {
    const element = await this.$el.querySelector("#unread-separator");
    if (element) {
      element.style.display = "none";
      this.$forceUpdate();
      element.remove();
    }
  }

  async addUnreadSeparator() {
    await this.removeUnreadSeparator();
    const listOfUnreadMessages = await this.$el.querySelectorAll(".unread");
    if (listOfUnreadMessages.length > 0) {
      const element = listOfUnreadMessages[listOfUnreadMessages.length - 1];

      const unreadSeparator = document.createElement("div");
      unreadSeparator.id = "unread-separator";
      unreadSeparator.innerHTML = this.$t("MESSAGES.UNREAD_SEPARATOR", {
        number: listOfUnreadMessages.length
      });

      element.after(unreadSeparator);
    }
  }

  async markMessagesAsRead() {
    const unreadMessages = this.messages.filter(
      m =>
        m.readAt == null &&
        m.senderUsersId == this.user.therapist.usersId &&
        m.eraseFromSender == false
    );

    let updatedMessages = 0;

    for (const message of unreadMessages) {
      if (message.messagesStatusesId == 2) {
        await this.changeMessagesStatus(3, message);
        await this.changeMessagesStatus(4, message);
        await this.getMessages();

        updatedMessages++;
      } else if (message.messagesStatusesId == 3) {
        await this.changeMessagesStatus(4, message);
        await this.getMessages();
        updatedMessages++;
      }
    }

    if (updatedMessages == unreadMessages.length) {
      this.removeUnreadSeparator();
    }
    this.globalStore.commit('setUnreadMessages', 0)
  }

  async changeMessagesStatus(
    messagesStatus: number,
    message: Record<string, any>
  ) {
    const data = {
      messagesStatusesId: messagesStatus,
      messageType: message.messageType,
      readAt: new Date().toISOString()
    };

    const changedMessage = await wrapper(
      this.apiService.markMessageAsRead(message.id, data, this.user.patientsId)
    );

    if (changedMessage.error) {
      if (changedMessage.error.status == 403) {
        this.globalStore.commit("open", {
          type: "danger",
          title: this.$t("ALERTS.MESSAGES.MARK_AS_READ_FORBIDDEN.FAILED.TITLE"),
          text: this.$t("ALERTS.MESSAGES.MARK_AS_READ_FORBIDDEN.FAILED.TEXT")
        });
      } else {
        this.globalStore.commit("open", {
          type: "danger",
          title: this.$t("ALERTS.MESSAGES.MARK_AS_READ.FAILED.TITLE"),
          text: this.$t("ALERTS.MESSAGES.MARK_AS_READ.FAILED.TEXT")
        });
      }
    }
  }

  async showSearchBox() {
    if (this.searchHidden) {
      this.searchDisabled = true;
      this.searchHidden = false;
      this.searchDisabled = false;
    } else {
      this.search();
    }
  }

  async search() {
    this.filteredMessages = this.messages.filter((m: any) => {
      return (
        m.body.toLowerCase().includes(this.searchString.toLowerCase()) &&
        !m.eraseFromSender
      );
    });
  }

  formatDate(date: Date) {
    return new Date(date).toLocaleString();
  }
}
